
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  formId?: string;
  additionalFields?: Record<string, any>;
  onFormSubmitted?: () => void;
}

export function ContactModal({
  isOpen,
  onClose,
  title = "Get in Touch",
  description = "Fill out the form below and we'll get back to you shortly.",
  formId = "dfa977cc-c726-4029-89d1-d8f757482a67", // Default contact form ID
  additionalFields = {},
  onFormSubmitted
}: ContactModalProps) {
  const [formLoaded, setFormLoaded] = useState(false);
  
  // Pre-load HubSpot script when component mounts
  useEffect(() => {
    if (!window.hbspt) {
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/v2.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  
  // Create form when modal opens
  useEffect(() => {
    if (isOpen && window.hbspt) {
      // Small timeout to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        const container = document.getElementById('hubspot-form-container');
        if (container) {
          // Clear any existing form
          container.innerHTML = '';
          
          // Create new form
          window.hbspt.forms.create({
            region: 'na1',
            portalId: '3377390',
            formId: formId,
            target: '#hubspot-form-container',
            inlineMessage: false,
            onFormSubmitted: () => {
              // Show success message
              toast.success("Your message has been sent successfully!");
              
              // Call the callback if provided
              if (onFormSubmitted) {
                onFormSubmitted();
              }
              
              // Close modal after a short delay
              setTimeout(onClose, 1500);
            },
            onFormReady: () => {
              setFormLoaded(true);
            },
            hiddenFields: additionalFields || {},
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, formId, onClose, onFormSubmitted, additionalFields]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] md:max-h-[85vh] overflow-hidden border border-purple-200/60 bg-white/95 backdrop-blur-md shadow-xl transition-all">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-purple-50/30 to-purple-100/50 z-0 rounded-md pointer-events-none opacity-80" aria-hidden="true" />
        
        <DialogHeader className="relative z-10">
          <DialogTitle className="text-xl font-semibold text-edgefi-purple bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst bg-clip-text text-transparent">{title}</DialogTitle>
          <DialogDescription className="text-gray-600">{description}</DialogDescription>
        </DialogHeader>
        
        {isOpen && (
          <div className="mt-4 relative z-10 overflow-hidden">
            <div className="w-full min-h-[300px] overflow-y-auto max-h-[60vh] md:max-h-[50vh] pr-1 -mr-1 form-container">
              <div 
                id="hubspot-form-container" 
                className="edgefi-hubspot-form"
              ></div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
